import { useId, type ReactElement, type ReactNode } from "react";
import { cloneElement } from "react";

import { cx } from "../lib/format";

export interface FieldProps {
  label: string;
  /** Plain-language explanation under the control ("Knowledge", not "vector store"). */
  help?: ReactNode;
  /** Inline error — say what happened and how to fix it. */
  error?: string | null;
  /** Right-aligned slot beside the label, e.g. a character counter. */
  trailing?: ReactNode;
  required?: boolean;
  className?: string;
  /** A single form control; Field wires up id/aria for it. */
  children: ReactElement;
}

export function Field({ label, help, error, trailing, required, className, children }: FieldProps) {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  const describedBy =
    [error ? errorId : null, help ? helpId : null].filter(Boolean).join(" ") || undefined;

  const childProps = children.props as Record<string, unknown>;
  const control = cloneElement(children, {
    id: (childProps.id as string | undefined) ?? id,
    "aria-describedby": describedBy,
    invalid: childProps.invalid ?? Boolean(error),
  } as Record<string, unknown>);

  return (
    <div className={cx("flex flex-col gap-1.5", className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-xs font-medium text-body">
          {label}
          {required && <span className="ml-1 text-muted">required</span>}
        </label>
        {trailing && <span className="text-xs text-muted">{trailing}</span>}
      </div>
      {control}
      {error ? (
        <p id={errorId} className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : help ? (
        <p id={helpId} className="text-xs text-muted">
          {help}
        </p>
      ) : null}
    </div>
  );
}
